'use client';

import './lead-form.scss';
import React, { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { CountryCode } from '@sola/schema/dotcom/shared/geo.ts';
import { StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import type { AdditionalSettingsFormDynamic } from '@sola/schema/lib/schema.ts';
import type { LeadChurchRequest } from '@sola/schema/lib/skippy.ts';
import { LeadFormChurchCityMaxLength, LeadFormChurchNameMaxLength, LeadFormFirstNameMaxLength, LeadFormLastNameMaxLength } from '@sola/skippy-sdk/constants.ts';
import { trackLeadFormSuccess } from '@sola/tracking/components/datalayer.tsx';
import { camelCaseToLabel } from '@sola/utils/string.ts';
import { AssetLinksFooter } from '../asset-links.tsx';
export type LeadFormProps = {
  onSubmit: (data: LeadChurchRequest) => Promise<boolean>;
  config?: Partial<AdditionalSettingsFormDynamic>;
};
const LeadFormState = z.object({
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional(),
  showThankYou: z.boolean().optional()
});
type LeadFormState = z.infer<typeof LeadFormState>;
const makeOption = (value: string) => {
  return {
    text: camelCaseToLabel(value),
    value
  };
};
export const LeadForm: React.FC<LeadFormProps> = ({
  onSubmit,
  config
}) => {
  const {
    t
  } = useTranslation(['forms', 'skippy']);
  const [formState, setFormState] = useState<LeadFormState>({});
  const defaultCountry = 'US' as CountryCode;
  const shouldValidate = true;
  const roleOptions = [{
    text: t('skippy:form.roleOptions.defaultText'),
    value: ''
  }, ...(config?.roles && config.roles.length > 0 ? config.roles! : [t('skippy:form.roleOptions.pastor'), t('skippy:form.roleOptions.churchStaff'), t('skippy:form.roleOptions.congregant')]).map(makeOption)];
  const sizeOptions = [{
    text: t('skippy:form.sizeOptions.defaultText'),
    value: ''
  }, ...['1-49', '50-99', '100-199', '200-299', '300-399', '400-499', '500-749', '750-999', '1,000', '2,000', '3,000', '4,000', '5,000', '6,000', '7,000', '8,000', '9,000', '10,000+'].map(makeOption)];
  const stateOptions = [{
    text: t('skippy:form.stateOptions.defaultText'),
    value: ''
  }, ...Object.entries(StateEnum).map(([abbr, name]) => ({
    text: name,
    value: abbr
  }))];
  const handleSubmit = async (data: FieldValues) => {
    let errorMessage = '';
    setFormState(prevState => ({
      ...prevState,
      errorMessage,
      isSubmitting: true
    }));
    try {
      const success = await onSubmit(data as LeadChurchRequest);
      if (success) {
        trackLeadFormSuccess(config?.subscriptionCode ?? 'Lead Form');
        setFormState({
          showThankYou: true,
          isSubmitting: false
        });
        return;
      }
      errorMessage = t('skippy:form.errorVerify');
    } catch {
      errorMessage = t('skippy:form.errorSave');
    }
    if (errorMessage) {
      setFormState({
        isSubmitting: false,
        errorMessage
      });
    }
  };
  if (formState.showThankYou) {
    const assets = config?.thankYouAssets;
    return <Alert variant="positive" message={config?.messageThankYou ?? t('skippy:form.leadCreate.thankYou')} buttonAction={assets && assets.length > 0 ? <AssetLinksFooter assets={assets} /> : undefined} noIcon scrollBehavior="instant" scrollPosition="center" scrollIntoView />;
  }
  return <div className="LeadForm max-w-sm">
      {formState.errorMessage && <div className="mb-comfy-xs">
          <Alert className="scroll-mt-roomy-md" variant="negative" message={formState.errorMessage} scrollIntoView />
        </div>}

      <Form className="flex flex-col gap-comfy-sm @sm:gap-comfy-lg" onSubmit={handleSubmit}>
        <Fieldset>
          <div className="grid grid-cols-1 @sm:grid-cols-2 gap-comfy-sm">
            <InputField label={t('forms:contactFields.firstNameLabel')} name="firstName" required maxLength={LeadFormFirstNameMaxLength} />
            <InputField label={t('forms:contactFields.lastNameLabel')} name="lastName" required maxLength={LeadFormLastNameMaxLength} />
          </div>
          {!config?.role?.hide && <Select label={t('forms:contactFields.roleLabel')} name="role" required={config?.role?.required} items={roleOptions} />}
          <EmailField name="email" label={t('forms:contactFields.emailLabel')} required />
          {!config?.phone?.hide && <PhoneField label={t('forms:contactFields.phoneNumberLabel')} name="phone" countryCode={defaultCountry} required={config?.phone?.required} />}
        </Fieldset>
        {config?.hasChurch && <Fieldset>
            <InputField label={t('forms:churchFields.churchNameLabel')} name="churchName" required maxLength={LeadFormChurchNameMaxLength} />
            <InputField label={t('forms:churchFields.churchAddressLabel')} name="churchAddress" required />
            <InputField label={t('forms:churchFields.churchCityLabel')} name="churchCity" required maxLength={LeadFormChurchCityMaxLength} />
            <div className="grid grid-cols-1 @sm:grid-cols-2 gap-comfy-sm">
              <StateField label={t('forms:churchFields.churchStateLabel')} name="churchState" autoComplete="address-level1" required items={stateOptions} isUS />

              <PostalCodeField label={t('forms:churchFields.churchPostalLabel')} name="churchPostal" autoComplete="postal-code" countryCode={defaultCountry} shouldValidate={shouldValidate} required />
            </div>

            <Select label={t('forms:churchFields.churchSizeLabel')} name="memberSize" required items={sizeOptions} />

            {!config?.website?.hide && <InputField label={t('forms:churchFields.churchWebsiteLabel')} name="churchWebsite" required={config?.website?.required} type="url" />}
          </Fieldset>}

        {!config?.comments?.hide &&
      // @TODO: textarea support
      <InputField label={t('forms:contactFields.commentsLabel')} name="comments" required={config?.comments?.required} />}

        {config?.consent && <div className="mt-comfy-xs">
            <Checkbox name="consent" label={config?.consentText ?? t('forms:consentText')} required />
          </div>}
        <FormSubmit className={cn({
        'mt-comfy-lg': !config?.consent,
        'mt-comfy-xs': !!config?.consent
      })} isBusy={formState.isSubmitting} iconAfter={<ArkIcon iconName="arrow-right" />}>
          {config?.buttonText ?? t('skippy:form.leadCreate.buttonText')}
        </FormSubmit>
      </Form>
    </div>;
};